import type {Size} from "../../_types/Size";
import type {WebGLRenderObject} from "../../_types/WebGLRenderObject";
import type {WebGLRenderTarget} from "../../_types/WebGLRenderTarget";
import { Color, rgb } from "../customColor";
import {createBuffer} from "./createBuffer";
import {createProgram} from "./createProgram";
import {createRenderTarget} from "./createRenderTarget";
import type {CreateRenderTargetOptions} from "./createRenderTarget";
import {createShader} from "./createShader";
import {createTexture} from "./createTexture";
import type {TextureSource} from "./createTexture";

type Uniform = {
    type: number;
    textureIndex?: number;
    value: WebGLTexture | Array<number> | number;
};

const QUAD: { uvs: Array<number>; vertices: Array<number> } = {
    uvs: [
        0, 1,
        0, 0,
        1, 0,
        0, 1,
        1, 0,
        1, 1
    ],
    vertices: [
        -0.5, +0.5,
        -0.5, -0.5,
        +0.5, -0.5,
        -0.5, +0.5,
        +0.5, -0.5,
        +0.5, +0.5
    ]
};

export class WebGLContext {

    private currentProgram?: WebGLProgram;
    private currentRenderTarget?: WebGLRenderTarget;

    private readonly canvas: HTMLCanvasElement;

    private readonly gl: WebGLRenderingContext;
    private readonly ext: ANGLE_instanced_arrays;

    private readonly uvBuffer: WebGLBuffer;
    private readonly vertexPositionBuffer: WebGLBuffer;

    private readonly colorBuffer: WebGLBuffer;
    private readonly scaleBuffer: WebGLBuffer;
    private readonly positionBuffer: WebGLBuffer;
    private readonly rotationBuffer: WebGLBuffer;

    private readonly shaderCache: Map<string, WebGLShader> = new Map();
    private readonly programCache: Map<string, WebGLProgram> = new Map();
    private readonly textureCache: Map<string, WebGLTexture> = new Map();
    private readonly renderTargetCache: Map<string, WebGLRenderTarget> = new Map();

    constructor() {

        this.canvas = document.createElement("canvas");
        const gl = this.canvas.getContext("webgl", {
            antialias: true,
            alpha: true,
            premultipliedAlpha: false,
            preserveDrawingBuffer: true
        });

        if (!gl) {
            throw new Error("Failed to get 'webgl' context.");
        }

        const ext = gl.getExtension("ANGLE_instanced_arrays");

        if (!ext) {
            throw new Error("Failed to get 'ANGLE_instanced_arrays' extension");
        }

        this.uvBuffer = createBuffer(gl, new Float32Array(QUAD.uvs));
        this.vertexPositionBuffer = createBuffer(gl, new Float32Array(QUAD.vertices));

        this.colorBuffer = createBuffer(gl, new Float32Array(4096 * 4));
        this.scaleBuffer = createBuffer(gl, new Float32Array(4096 * 2));
        this.positionBuffer = createBuffer(gl, new Float32Array(4096 * 2));
        this.rotationBuffer = createBuffer(gl, new Float32Array(4096));

        this.ext = ext;
        this.gl = gl;

        this.setPremultipliedAlpha(false);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public clear(color: Color = { mode: "rgb", r: 0, g: 0, b: 0 }): void {
        const { r, g, b, alpha } = rgb(color);
        this.gl.clearColor(r, g, b,  alpha ?? 1);
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public setPremultipliedAlpha(value: boolean) {
        this.gl.enable(this.gl.BLEND);
        this.gl.blendFunc(value ? this.gl.ONE : this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public render(objects: WebGLRenderObject | Array<WebGLRenderObject>): void {

        if (!this.currentProgram) {
            console.warn("Tried to render without active program, skipping...");
            return;
        }

        const { gl, ext } = this;

        if (!Array.isArray(objects)) {
            objects = [objects];
        }

        if (objects.length === 0) {
            return;
        }

        const objectsByTexture: Map<WebGLTexture, Array<WebGLRenderObject>> = new Map();

        for (const object of objects
            .filter(o => isInBounds(this.currentRenderTarget ?? this.canvas, o))) {

            if (objectsByTexture.has(object.texture)) {
                objectsByTexture.get(object.texture)!.push(object);
            } else {
                objectsByTexture.set(object.texture, [object]);
            }
        }

        this.setUniform("resolution", {
            type: gl.FLOAT_VEC2,
            value: [
                this.currentRenderTarget?.width ?? this.canvas.width,
                this.currentRenderTarget?.height ?? this.canvas.height
            ]
        });

        this.setUniform("projection", {
            type: gl.FLOAT_MAT4,
            value: getOrthographicProjectionMatrix(
                this.currentRenderTarget?.width ?? this.canvas.width,
                this.currentRenderTarget?.height ?? this.canvas.height
            )
        });

        const textureUniformLocation = gl.getUniformLocation(this.currentProgram, "texture");

        for (const [texture, objects] of objectsByTexture.entries()) {

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.uniform1i(textureUniformLocation, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);

            gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Float32Array(
                objects
                    // @ts-ignore
                    .map(o => rgb(o.color))
                    .map(({r, g, b, alpha}) => [r, g, b, alpha ?? 1]).flat()));

            gl.bindBuffer(gl.ARRAY_BUFFER, this.scaleBuffer);
            gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Float32Array(
                objects.map(o => [o.width, o.height]).flat()));

            gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
            gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Float32Array(
                objects.map(o => [o.x, o.y]).flat()));

            gl.bindBuffer(gl.ARRAY_BUFFER, this.rotationBuffer);
            gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Float32Array(
                objects.map(o => o.rotation)));

            ext.drawArraysInstancedANGLE(gl.TRIANGLES, 0, QUAD.vertices.length / 2, objects.length);
        }
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public useProgram(program: WebGLProgram): void {

        const { gl, ext } = this;

        if (this.currentProgram === program) {
            return;
        }

        gl.useProgram(program);
        this.currentProgram = program;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
        const uvAttributeLocation = gl.getAttribLocation(program, "uv");
        gl.vertexAttribPointer(uvAttributeLocation, 2, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(uvAttributeLocation);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexPositionBuffer);
        const vertexPositionAttributeLocation = gl.getAttribLocation(program, "vertex_position");
        gl.vertexAttribPointer(vertexPositionAttributeLocation, 2, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(vertexPositionAttributeLocation);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
        const colorAttributeLocation = gl.getAttribLocation(program, "color");
        gl.vertexAttribPointer(colorAttributeLocation, 4, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(colorAttributeLocation);
        ext.vertexAttribDivisorANGLE(colorAttributeLocation, 1);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.scaleBuffer);
        const scaleAttributeLocation = gl.getAttribLocation(program, "scale");
        gl.vertexAttribPointer(scaleAttributeLocation, 2, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(scaleAttributeLocation);
        ext.vertexAttribDivisorANGLE(scaleAttributeLocation, 1);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        const positionAttributeLocation = gl.getAttribLocation(program, "position");
        gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(positionAttributeLocation);
        ext.vertexAttribDivisorANGLE(positionAttributeLocation, 1);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.rotationBuffer);
        const rotationAttributeLocation = gl.getAttribLocation(program, "rotation");
        gl.vertexAttribPointer(rotationAttributeLocation, 1, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(rotationAttributeLocation);
        ext.vertexAttribDivisorANGLE(rotationAttributeLocation, 1);
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public useRenderTarget(renderTarget?: WebGLRenderTarget): void {

        const { gl } = this;

        if (renderTarget) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, renderTarget.framebuffer);
            gl.viewport(0, 0, renderTarget.width, renderTarget.height);
        } else {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null as WebGLFramebuffer|null);
            gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        }

        this.currentRenderTarget = renderTarget;
    }

    /**
     * Sets a global "const" for the current program which can be used by the vertex and fragment shader.
     */
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public setUniform(name: string, value: Uniform): void {

        if (!this.currentProgram) {
            console.warn(`Tried to set uniform '${name}' without active program, skipping...`);
            return;
        }

        const { gl } = this;

        const uniformLocation = gl.getUniformLocation(this.currentProgram, name);

        if (value.value instanceof WebGLTexture) {

            if (value.textureIndex === undefined) {
                console.warn(`Tried to set uniform '${name}' of type Sampler2D, but no textureIndex was supplied`);
                return;
            }

            // add one since we always set texture index 0 to the texture of the object
            gl.activeTexture(gl.TEXTURE0 + 1 + value.textureIndex);
            gl.bindTexture(gl.TEXTURE_2D, value.value);
            gl.uniform1i(uniformLocation, 1 + value.textureIndex);

            return;
        }

        if (!Array.isArray(value.value)) {
            value.value = [value.value];
        }

        switch (value.type) {
            case gl.FLOAT:
                gl.uniform1fv(uniformLocation, value.value as Array<number>);
                break;
            case gl.FLOAT_VEC2:
                gl.uniform2fv(uniformLocation, value.value as Array<number>);
                break;
            case gl.FLOAT_MAT4:
                gl.uniformMatrix4fv(uniformLocation, false, value.value as Array<number>);
                break;
            default:
                throw new Error(`No setUniform implementation for gl type '${value.type}'.`);
        }
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public createProgram(
        source: { vertexShader: string; fragmentShader: string },
        key: string = `${source.vertexShader}_${source.fragmentShader}`
    ): WebGLProgram {

        if (this.programCache.has(key)) {
            return this.programCache.get(key)!;
        }

        const vertexShader = this.createShader(
            this.gl.VERTEX_SHADER,
            source.vertexShader,
            `__p_${source.vertexShader}`
        );

        const fragmentShader = this.createShader(
            this.gl.FRAGMENT_SHADER,
            source.fragmentShader,
            `__p_${source.fragmentShader}`
        );

        const program = createProgram(this.gl, { vertexShader, fragmentShader });
        this.programCache.set(key, program);
        return program;
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public createShader(
        type: number,
        source: string,
        key: string = `${source}`
    ): WebGLShader {

        if (this.shaderCache.has(key)) {
            return this.shaderCache.get(key)!;
        }

        const shader = createShader(this.gl, type, source);
        this.shaderCache.set(key, shader);
        return shader;
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public createTexture(
        source: TextureSource,
        key: string = `${source instanceof HTMLImageElement
            ? source.src
            : source.data?.join(",")}_${source.width}x${source.height}`
    ): WebGLTexture {

        if (this.textureCache.has(key)) {
            return this.textureCache.get(key)!;
        }

        const texture = createTexture(this.gl, source);
        this.textureCache.set(key, texture);
        return texture;
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public createRenderTarget(
        options: Omit<CreateRenderTargetOptions, "texture">,
        key: string = `${options.width}x${options.height}`
    ): WebGLRenderTarget {

        if (this.renderTargetCache.has(key)) {
            return this.renderTargetCache.get(key)!;
        }

        const texture = this.createTexture(options, `__rt_${key}`);
        const renderTarget = createRenderTarget(this.gl, { ...options, texture });
        this.renderTargetCache.set(key, renderTarget);
        return renderTarget;
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public resize(width: number, height: number): void {

        const { canvas, gl } = this;

        canvas.width = width;
        canvas.height = height;

        gl.viewport(0, 0, width, height);
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public getCanvas(): HTMLCanvasElement {
        return this.canvas;
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public dispose(): void {

        const { gl } = this;

        this.shaderCache.forEach(shader => {
            gl.deleteShader(shader);
        });

        this.shaderCache.clear();

        this.programCache.forEach(program => {
            // attached shaders are deleted by shader cache
            gl.deleteProgram(program);
        });

        this.programCache.clear();

        this.textureCache.forEach(texture => {
            gl.deleteTexture(texture);
        });

        this.textureCache.clear();

        this.renderTargetCache.forEach(target => {
            // texture is deleted by texture cache
            gl.deleteFramebuffer(target.framebuffer);
        });

        this.renderTargetCache.clear();
    }
}

const isInBounds = (bounds: Size, object: WebGLRenderObject): boolean => {

    const halfWidth = object.width / 2;
    const halfHeight = object.height / 2;

    const corners = [
        { x: -halfWidth, y: -halfHeight },
        { x: +halfWidth, y: -halfHeight },
        { x: +halfWidth, y: +halfHeight },
        { x: -halfWidth, y: +halfHeight }
    ];

    const cos = Math.cos(object.rotation);
    const sin = Math.sin(object.rotation);

    const rotatedCorners = corners.map(corner => ({
        x: object.x + (corner.x * cos - corner.y * sin),
        y: object.y + (corner.x * sin + corner.y * cos)
    }));

    const minX = Math.min(...rotatedCorners.map(c => c.x));
    const maxX = Math.max(...rotatedCorners.map(c => c.x));
    const minY = Math.min(...rotatedCorners.map(c => c.y));
    const maxY = Math.max(...rotatedCorners.map(c => c.y));

    return minX <= bounds.width
        || maxX >= 0
        || minY <= bounds.height
        || maxY >= 0;
};

const getOrthographicProjectionMatrix = (width: number, height: number): Array<number> => {

    const matrix = Array.from({ length: 4 }, () => new Array(4).fill(0));

    matrix[0][0] = 2 / width;
    matrix[1][1] = -2 / height;
    matrix[2][2] = -1;

    matrix[3][0] = -1;
    matrix[3][1] = 1;
    matrix[3][2] = 0;
    matrix[3][3] = 1;

    return matrix.flat();
};